import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import WideContainer from "../../components/WideContainer";
import CircularProgress from "@mui/material/CircularProgress";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Player, ControlBar, BigPlayButton, LoadingSpinner, PlayToggle, VolumeMenuButton, FullscreenToggle, CurrentTimeDisplay, DurationDisplay, ProgressControl } from "video-react";
import { withTranslation } from "react-i18next";

import "../../../node_modules/video-react/dist/video-react.css";
import { fetchScenes, sceneLang } from "./scenes/scenesApi";
import { dragColumns } from "./scenes/columns";
import { buildModel, matchScene, hasFilter, countMatches, arcDetail, characterDetail, screenTime, castTimes, sceneAt, memoize } from "./scenes/sceneModel";
import StoryMap from "./scenes/StoryMap";
import { SceneRow, SceneCard } from "./scenes/SceneCard";
import { ArcCard, CharacterCard } from "./scenes/DetailCards";
import { pressable } from "../../app/a11y";
import "./Queries.css";
import "./Scenes.css";

// column widths are automatic (see .sc-body in Scenes.css) until the user drags the grip (see scenes/columns.js)

const NO_SCENES = {};

class Index extends React.Component {
  constructor(props) {
    super(props);
    // filters opened from a link (the Overview tab): ?tag= (content tag), ?arc= (arc index), ?who= (character name)
    const link = new URLSearchParams((props.location && props.location.search) || "");
    const linkArc = link.get("arc");
    const linkWho = link.get("who") || null;
    this.state = {
      loading: true,
      data: null,
      sel: 0,
      open: linkWho ? { 0: false } : {}, // a character link opens with every scene collapsed, the selected one too
      tr: {},
      query: "",
      tag: link.get("tag") || null,
      lanes: linkWho ? "chars" : null, // null = arcs if the video has any, set when the scenes load
      arc: linkArc !== null && /^\d+$/.test(linkArc) ? Number(linkArc) : null,
      arcOpen: {}, // scene index -> expanded under its step in the arc card
      who: linkWho,
      arcsAll: false,
      charsAll: false,
      currentTime: props.startTime > 0 ? props.startTime : -1,
      leftW: null, // dragged left column width, null = automatic
      resizing: false,
    };

    this.byLang = {}; // lang -> normalized /scenes response
    this.listRef = React.createRef();
    this.lastScroll = 0;
    this.autoScrollAt = 0;
    this.seekAt = 0;
    this.scrollOnSelect = false;
    // opened at a time (another tab's player, a search result): select its scene once the scenes load
    this.pendingTime = props.startTime > 0 ? props.startTime : null;

    this.getModel = memoize((data, sentences, dic) => (data ? buildModel(data, sentences, dic) : null));
    this.getMatches = memoize((model, tag, q, arc, who) => {
      const f = { tag, q, arc, who };
      return model && hasFilter(f) ? model.scenes.map((x) => matchScene(model, x, f)) : null;
    });
    this.getHits = memoize((model, q) => (model && q ? model.scenes.map((x) => countMatches(x, q)) : null));
    this.getArc = memoize((model, k) => (model && k !== null ? arcDetail(model, k) : null));
    this.getWho = memoize((model, name) => (model && name ? characterDetail(model, name) : null));
    // project character (from the characters slice) behind a scene character: by char id, else by name
    this.getCharacter = memoize(
      (items, who) =>
        (who && items.find((c) => (who.id !== null && who.id !== undefined ? String(c.char_id) === String(who.id) : c.char_name === who.name))) || null
    );
    this.getScreenTime = memoize((item) => (item ? screenTime(item.tracks_data) : null));
    this.getCastTimes = memoize(castTimes);
    this.getFolder = memoize((projectFolder) => JSON.parse(localStorage.getItem("user")).user_folder + "/" + projectFolder);
  }

  componentDidMount() {
    this.player.subscribeToStateChange(this.handleStateChange.bind(this));
    this.getItem();
    // the link's filters are in the state now; the address keeps the filters on (and drops ?t=)
    this.syncAddress();
  }

  // the content tag, arc and character filters in the address (?tag=&arc=&who=), so that they can be shared and
  // come back with Back
  syncAddress() {
    const { location, history } = this.props;
    if (!location || !history) return;
    const p = new URLSearchParams();
    if (this.state.tag) p.set("tag", this.state.tag);
    if (this.state.arc !== null) p.set("arc", String(this.state.arc));
    if (this.state.who) p.set("who", this.state.who);
    const search = p.toString() ? "?" + p : "";
    if (location.search !== search) history.replace(location.pathname + search);
  }

  matches() {
    const { tag, query, arc, who } = this.state;
    return this.getMatches(this.model(), tag, query.trim().toLowerCase(), arc, arc === null ? who : null);
  }

  // scroll the list to scene i (first matching scene, selected scene)
  scrollToScene(i) {
    const el = this.listRef.current;
    const card = el && el.querySelector('[data-i="' + i + '"]');
    if (!card) return;
    this.autoScrollAt = new Date().getTime();
    el.scrollTop = Math.max(0, el.scrollTop + card.getBoundingClientRect().top - el.getBoundingClientRect().top - 8);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.videosrc !== prevProps.videosrc) {
      this.player.load();
    }
    if (this.lang() !== this.dataLang) this.getItem();
    if (prevState.sel !== this.state.sel && this.scrollOnSelect) {
      this.scrollOnSelect = false;
      this.scrollToSelected();
    }
    // the arc card replaces the scene list: open it at the top, come back to the selected scene
    if (prevState.arc !== this.state.arc) {
      if (this.state.arc === null) this.scrollToSelected();
      else if (this.listRef.current) this.listRef.current.scrollTop = 0;
    }
    if (prevState.tag !== this.state.tag || prevState.arc !== this.state.arc || prevState.who !== this.state.who) this.syncAddress();
    const model = this.model();
    // opened at a time: select the scene playing then
    if (model && this.pendingTime !== null) {
      const i = sceneAt(model, this.pendingTime);
      this.pendingTime = null;
      if (i >= 0 && i !== this.state.sel) {
        this.scrollOnSelect = true;
        this.setState((st) => ({ sel: i, open: { ...st.open, [st.sel]: st.open[st.sel] === undefined ? false : st.open[st.sel] } }));
      }
    }
    // a new filter: bring the first matching scene into view when the selected one doesn't match
    const matches = this.matches();
    if (matches !== this.lastMatches) {
      this.lastMatches = matches;
      if (matches && this.state.arc === null && !matches[this.state.sel]) {
        const first = matches.findIndex(Boolean);
        if (first >= 0) this.scrollToScene(first);
      }
    }
    if (model && model !== this.reportedModel) {
      this.reportedModel = model;
      this.props.onStats &&
        this.props.onStats({ scenes: model.scenes.length, arcs: model.arcs.length, characters: model.characterNames.length, duration: model.duration });
    }
  }

  componentWillUnmount() {
    this.stopDrag && this.stopDrag();
  }

  lang() {
    return sceneLang(this.props.i18n);
  }

  // scenes in the UI language; the current ones stay on screen while another language loads
  getItem() {
    const lang = this.lang();
    this.dataLang = lang;
    if (this.byLang[lang]) {
      this.setState({ data: this.byLang[lang] });
      return;
    }
    if (!this.state.data) this.setState({ loading: true });
    fetchScenes(this.props.project.project_id, lang)
      .then(
        function (data) {
          this.byLang[lang] = data;
          if (lang !== this.dataLang) return;
          this.setState((st) => ({ loading: false, data: data, lanes: st.lanes || (data.arcs.length ? "arcs" : "chars") }));
        }.bind(this)
      )
      .catch(
        function (error) {
          this.setState({ loading: false });
          console.log(error);
        }.bind(this)
      );
  }

  model() {
    return this.getModel(this.state.data, this.props.sentences.items, this.props.characters.dic);
  }

  handleStateChange(state) {
    const t = state.currentTime;
    // share the time with the other tabs (once the video has a time of its own)
    if (this.props.onTime && (state.hasStarted || t > 0)) this.props.onTime(t);
    if (Math.abs(t - this.state.currentTime) >= 0.1) this.setState({ currentTime: t });

    // follow playback into the next scene (not right after a seek from the tab)
    if (state.paused || state.seeking || new Date().getTime() - this.seekAt < 1000) return;
    const model = this.model();
    if (!model) return;
    const cur = model.scenes[this.state.sel];
    if (cur && t >= cur.s && t < cur.e) return;
    const i = sceneAt(model, t);
    if (i < 0 || i === this.state.sel) return;
    //do not scroll when user scrolled in the last 3 seconds
    this.scrollOnSelect = new Date().getTime() - this.lastScroll > 3000;
    this.setState({ sel: i });
  }

  scrollToSelected() {
    this.scrollToScene(this.state.sel);
  }

  onListScroll = () => {
    if (new Date().getTime() - this.autoScrollAt > 300) this.lastScroll = new Date().getTime();
  };

  isOpen(i) {
    const o = this.state.open[i];
    return o === true || (o === undefined && i === this.state.sel);
  }

  // select a scene (and seek to t, default scene start); the previously selected scene stays open
  select = (i, t) => {
    const model = this.model();
    const x = model && model.scenes[i];
    if (!x) return;
    const time = t === undefined ? x.s : t;
    this.scrollOnSelect = i !== this.state.sel;
    this.setState((st) => ({
      sel: i,
      open: { ...st.open, [st.sel]: st.open[st.sel] === undefined ? true : st.open[st.sel], [i]: true },
      currentTime: time,
    }));
    this.seekAt = new Date().getTime();
    this.player.seek(time);
  };

  play = (i) => {
    this.select(i);
    this.player.play();
  };

  collapse = (i) => this.setState((st) => ({ open: { ...st.open, [i]: false } }));
  toggleTr = (i) => this.setState((st) => ({ tr: { ...st.tr, [i]: !st.tr[i] } }));
  toggleTag = (g) => this.setState((st) => ({ tag: st.tag === g ? null : g }));
  toggleArc = (k) => this.setState((st) => ({ arc: st.arc === k ? null : k, arcOpen: {} }));
  toggleWho = (n) => this.setState((st) => ({ who: st.who === n ? null : n }));
  showWho = (n) => this.setState({ who: n });
  setQuery = (e) => this.setState({ query: e.target.value });
  clearQuery = () => this.setState({ query: "" });
  setLanes = (lanes) => this.setState({ lanes });
  toggleArcs = () => this.setState((st) => ({ arcsAll: !st.arcsAll }));
  toggleChars = () => this.setState((st) => ({ charsAll: !st.charsAll }));
  closeArc = () => this.setState({ arc: null, who: null, arcOpen: {} });
  closeWho = () => this.setState({ who: null });
  // arc step: expand its scene card under the step and select the scene, or collapse it
  toggleStep = (i) => {
    if (this.state.arcOpen[i]) return this.collapseStep(i);
    this.setState((st) => ({ arcOpen: { ...st.arcOpen, [i]: true } }));
    this.select(i);
  };
  collapseStep = (i) => this.setState((st) => ({ arcOpen: { ...st.arcOpen, [i]: false } }));

  startResize = (e) => {
    this.setState({ resizing: true });
    this.stopDrag = dragColumns(
      e,
      (w) => this.setState({ leftW: w }),
      () => this.setState({ resizing: false })
    );
  };
  resetResize = () => this.setState({ leftW: null });

  // the project's folder on the CDN
  folder() {
    return this.getFolder(this.props.project.project_folder);
  }

  thumbUrl(item) {
    const track = item && item.rep_track_id;
    if (track === undefined || track === null || track === -1) return null;
    return this.folder() + "/tracks_thumbnails/" + track + ".jpg";
  }

  render() {
    const { t } = this.props;
    const { sel, query, tag, arc, who, loading, leftW } = this.state;
    const model = this.model();
    const q = query.trim().toLowerCase();
    // an open arc filters by its scenes alone
    const matches = this.getMatches(model, tag, q, arc, arc === null ? who : null);
    const hits = this.getHits(model, q);
    const arcD = this.getArc(model, arc);
    const whoD = this.getWho(model, who);
    const whoItem = this.getCharacter(this.props.characters.items, whoD);
    const scenes = model ? model.scenes : [];
    const matchCount = matches ? matches.filter(Boolean).length : null;

    const folder = this.folder();
    const common = (x) => ({ t: t, projectId: this.props.project.project_id, folder: folder, x: x, q: q, onSelect: this.select, onPlay: this.play });
    const sceneCard = (x, dim, onCollapse) => (
      <SceneCard
        key={x.i}
        {...common(x)}
        dim={dim}
        sel={x.i === sel}
        currentTime={x.i === sel ? this.state.currentTime : -1}
        arcs={model.arcs}
        arc={arc}
        who={who}
        trOpen={!!this.state.tr[x.i]}
        onCollapse={onCollapse}
        onToggleTr={this.toggleTr}
        onArc={this.toggleArc}
        onWho={this.toggleWho}
      />
    );
    // scene cards expanded under their arc steps; a constant when none are, so the arc card can skip renders
    let stepScenes = NO_SCENES;
    if (arcD && arcD.steps.some((s) => this.state.arcOpen[s.i])) {
      stepScenes = {};
      arcD.steps.forEach((s) => this.state.arcOpen[s.i] && (stepScenes[s.i] = sceneCard(scenes[s.i], false, this.collapseStep)));
    }

    return (
      <div className="scenesTab">
        <WideContainer>
          <div className="sc-page" style={leftW === null ? undefined : { "--sc-drag": leftW + "px" }}>
            {scenes.length > 0 && (
              <StoryMap
                t={t}
                model={model}
                castTimes={this.getCastTimes(this.props.characters.items, this.props.sentences.items)}
                sel={sel}
                matches={matches}
                matchCount={matchCount}
                tag={tag}
                query={query}
                lanes={this.state.lanes}
                arc={arc}
                who={who}
                arcsAll={this.state.arcsAll}
                charsAll={this.state.charsAll}
                onTag={this.toggleTag}
                onQuery={this.setQuery}
                onClearQuery={this.clearQuery}
                onLanes={this.setLanes}
                onArc={this.toggleArc}
                onWho={this.toggleWho}
                onSelect={this.select}
                onToggleArcs={this.toggleArcs}
                onToggleChars={this.toggleChars}
              />
            )}
            <div className="sc-body">
              <div
                className="sc-grip"
                data-on={this.state.resizing}
                title={t("Drag to resize columns · double-click to reset")}
                onMouseDown={this.startResize}
                onDoubleClick={this.resetResize}
              />
              <div className="sc-col">
                <div className="sc-player">
                  <Player
                    ref={(player) => {
                      this.player = player;
                    }}
                    autoPlay={false}
                    startTime={this.props.startTime}
                    poster={this.props.startTime > 0 ? undefined : folder + "/video_lowres_thumbnail.jpg"}
                    src={this.props.videosrc}
                  >
                    <BigPlayButton position="center" />
                    <LoadingSpinner />
                    <ControlBar autoHide={false} disableDefaultControls>
                      <PlayToggle key="play-toggle" order={1} />
                      <VolumeMenuButton key="volume-menu-button" order={4} vertical />
                      <CurrentTimeDisplay key="current-time-display" order={5.1} />
                      <DurationDisplay key="duration-display" order={7.3} />
                      <ProgressControl key="progress-control" order={6} />
                      <FullscreenToggle key="fullscreen-toggle" order={8} />
                    </ControlBar>
                  </Player>
                </div>
                {whoD && (
                  <CharacterCard
                    key={"who" + who}
                    t={t}
                    who={whoD}
                    thumb={this.thumbUrl(whoItem)}
                    screenTime={this.getScreenTime(whoItem)}
                    onSelect={this.select}
                    onClose={this.closeWho}
                  />
                )}
              </div>
              <div className="sc-col sc-list" ref={this.listRef} onScroll={this.onListScroll}>
                {loading && <CircularProgress style={{ margin: "40px auto", display: "block" }} />}
                {!loading && scenes.length === 0 && <div className="sc-empty">{t("No scenes found")}</div>}
                {arcD && (
                  <>
                    <div style={{ flex: "none", display: "flex" }}>
                      <span className="sc-link sc-back" {...pressable(this.closeArc)}>
                        <ChevronLeftIcon />
                        {t("Back to scenes")}
                      </span>
                    </div>
                    <ArcCard
                      key={"arc" + arc}
                      t={t}
                      arc={arcD}
                      expanded={stepScenes}
                      onStep={this.toggleStep}
                      onWho={this.showWho}
                      onClose={this.closeArc}
                    />
                  </>
                )}
                {!arcD &&
                  scenes.map((x) => {
                    // the selected scene dims too when it doesn't match: its outline still shows it is selected
                    const dim = !!matches && !matches[x.i];
                    return this.isOpen(x.i) ? (
                      sceneCard(x, dim, this.collapse)
                    ) : (
                      <SceneRow key={x.i} {...common(x)} dim={dim} hits={hits ? hits[x.i] : 0} />
                    );
                  })}
              </div>
            </div>
          </div>
        </WideContainer>
      </div>
    );
  }
}

const mapState = (state) => {
  return {
    sentences: state.sentences,
    characters: state.characters,
  };
};
export default withTranslation()(withRouter(connect(mapState)(Index)));
